import { Button, Icon, Tooltip } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { subscriptions as subscriptionsService } from "../../api/subscriptions.service";
import type { SubscriptionWithFeatures } from "../../api/subscriptions.service";
import { contacts as contactsService } from "../../api/contacts.service";
import type { Contact } from "../../api/contacts.service";
import { crmWebhookLogs, type CrmWebhookLog } from "../../api/crm-webhook-logs.service";
import { firms as firmsService } from "../../api/firms.service";
import { offices as officesService } from "../../api/offices.service";
import type { Office } from "../../api/offices.service";
import { ConfirmationModal } from "../../components/Modal/ConfirmationModal";
import { UnsavedChangesModal } from "../../components/Modal/UnsavedChangesModal";
import { UnauthorizedView } from "../../components/UnauthorizedView";
import EmptyState from "../../components/EmptyState";
import LoadingState from "../../components/LoadingState";
import PermissionGate from "../../components/Permissions/PermissionGate";
import type { Feature } from "../../api/features.service";
import type { Subscription } from "../../schemas/subscriptionSchema";
import type { ToastType } from "@legal-500/legal500-design-system";
import { MODAL_OVERVIEW_FIELD_CONFIGS } from "./subscriptionConfig";
import { SubscriptionForm } from "./SubscriptionForm";
import { useCountryOptions, useSubscriptionMetadataEntities } from "./hooks";
import { OnboardingTab, type OnboardingData } from "./OnboardingTab";
import { MetadataFieldsEditor } from "./MetadataFieldsEditor";
import type { OfficeContactPair } from "../../components/EliteMetadataConfiguration";
import styles from "./SubscriptionDetail.module.css";

type SubscriptionDetailContext = {
  toast: { message: string; type: ToastType } | null;
  setToast: (toast: { message: string; type: ToastType } | null) => void;
  refetchTrigger?: number;
  onMutationSuccess?: () => void;
};

const STATUS_DESCRIPTIONS: Record<string, string> = {
  pending: "Subscription is awaiting setup",
  complete: "Subscription is live and active",
  cancelled: "Subscription has been cancelled",
};

function calculateDaysRemaining(endDate: string): number {
  const end = new Date(endDate);
  const today = new Date();
  const diffTime = end.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

function requiresOnboarding(subscription: SubscriptionWithFeatures): boolean {
  if (subscription.status === "cancelled") return false;
  return subscription.isFullyConfigured === false;
}

function renderFieldValue(subscription: Subscription, key: keyof Subscription): React.ReactNode {
  const field = MODAL_OVERVIEW_FIELD_CONFIGS.find(f => f.key === key);
  if (field?.render) {
    return field.render(subscription[key], subscription);
  }
  const value = subscription[key];
  return value !== null && value !== undefined ? String(value) : "";
}

function SubscriptionDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { setToast, onMutationSuccess } = useOutletContext<SubscriptionDetailContext>();

  const { countryOptions } = useCountryOptions();
  const [subscription, setSubscription] = useState<SubscriptionWithFeatures | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [onboardingError, setOnboardingError] = useState<string | null>(null);
  const [supplementaryErrors, setSupplementaryErrors] = useState<string[]>([]);

  const [placementRegionName, setPlacementRegionName] = useState<string | null>(null);
  const [billingRegionName, setBillingRegionName] = useState<string | null>(null);
  const [contactsMap, setContactsMap] = useState<Map<number, Contact>>(new Map());
  const [officesMap, setOfficesMap] = useState<Map<number, Office>>(new Map());
  const [webhookLog, setWebhookLog] = useState<CrmWebhookLog | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "features" | "metadata" | "onboarding">(
    "overview",
  );

  const {
    entityOptions: metadataEntityOptions,
    entityErrors: metadataEntityErrors,
    isLoadingEntities: isLoadingMetadataEntities,
    fieldLabel: metadataFieldLabel,
    labelForField: metadataLabelForField,
  } = useSubscriptionMetadataEntities(subscription);

  const [editingMetadataKey, setEditingMetadataKey] = useState<string | null>(null);
  const [metadataSelections, setMetadataSelections] = useState<Record<string, (number | string)[]>>(
    {},
  );
  const [metadataOfficeContactPairs, setMetadataOfficeContactPairs] = useState<OfficeContactPair[]>(
    [],
  );
  const [isSavingMetadata, setIsSavingMetadata] = useState(false);
  const [metadataSaveError, setMetadataSaveError] = useState<string | null>(null);
  const [editionToRemove, setEditionToRemove] = useState<number | string | null>(null);

  useEffect(() => {
    if (id) {
      loadSubscription(id);
    }
  }, [id]);

  useEffect(() => {
    if (countryOptions.length === 0 || !subscription) return;
    const placementCountry = countryOptions.find(
      c => c.value === String(subscription.siteStructureId),
    );
    setPlacementRegionName(placementCountry?.label ?? null);
    const billingCountry = countryOptions.find(
      c => c.value === String(subscription.billingTaxonomyTermId),
    );
    setBillingRegionName(billingCountry?.label ?? null);
  }, [countryOptions, subscription?.siteStructureId, subscription?.billingTaxonomyTermId]);

  useEffect(() => {
    async function fetchContactsForPairs() {
      const officeContactPairs = (subscription?.metadata as Record<string, unknown> | undefined)
        ?.office_contact_pairs as Array<{ office_id: number; contact_ids: number[] }> | undefined;
      if (!Array.isArray(officeContactPairs) || officeContactPairs.length === 0) {
        setContactsMap(new Map());
        return;
      }
      try {
        const officeIds = [...new Set(officeContactPairs.map(pair => pair.office_id))];
        const responses = await Promise.all(
          officeIds.map(officeId => contactsService.getAll({ officeWpId: officeId, limit: 500 })),
        );
        const contactMap = new Map<number, Contact>();
        for (const response of responses) {
          for (const contact of response.contacts) {
            contactMap.set(contact.contactId, contact);
          }
        }
        setContactsMap(contactMap);
      } catch (err) {
        setSupplementaryErrors(prev => [
          ...prev,
          `Failed to load contacts: ${err instanceof Error ? err.message : String(err)}`,
        ]);
      }
    }
    fetchContactsForPairs();
  }, [subscription]);

  useEffect(() => {
    async function fetchOfficesForPairs() {
      const officeContactPairs = (subscription?.metadata as Record<string, unknown> | undefined)
        ?.office_contact_pairs as Array<{ office_id: number; contact_ids: number[] }> | undefined;
      if (!Array.isArray(officeContactPairs) || officeContactPairs.length === 0) return;
      try {
        const officeIds = officeContactPairs.map(pair => pair.office_id);
        const fetchedOffices = await Promise.all(
          officeIds.map(officeId => officesService.getById(officeId)),
        );
        const newOfficesMap = new Map<number, Office>(
          fetchedOffices.map(office => [office.officeId, office]),
        );
        setOfficesMap(newOfficesMap);
      } catch (err) {
        setSupplementaryErrors(prev => [
          ...prev,
          `Failed to load offices: ${err instanceof Error ? err.message : String(err)}`,
        ]);
      }
    }
    fetchOfficesForPairs();
  }, [subscription]);

  useEffect(() => {
    async function fetchWebhookLog() {
      if (!subscription?.correlationId) {
        setWebhookLog(null);
        return;
      }
      try {
        const log = await crmWebhookLogs.getByCorrelationId(subscription.correlationId);
        setWebhookLog(log);
      } catch (err) {
        setWebhookLog(null);
        const status = (err as Error & { status?: number }).status;
        if (status !== 404) {
          setSupplementaryErrors(prev => [
            ...prev,
            `Failed to load webhook log: ${err instanceof Error ? err.message : String(err)}`,
          ]);
        }
      }
    }
    fetchWebhookLog();
  }, [subscription?.correlationId]);

  useEffect(() => {
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = "";
      }
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  async function loadSubscription(subscriptionId: string) {
    try {
      setLoading(true);
      setError(null);
      const data = await subscriptionsService.getById(subscriptionId, true);

      // Enrich with company details from address book
      let enriched: SubscriptionWithFeatures = data;
      if (data.firmId) {
        try {
          const firm = await firmsService.getById(Number(data.firmId));
          if (firm) {
            enriched = {
              ...data,
              companyName: firm.name,
              companySlug: firm.slug,
              companyCrmEntityNumber: firm.crmEntityNumber,
            };
          }
        } catch {
          // Company details are supplementary, don't fail the load
        }
      }

      setSubscription(enriched);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load subscription"));
    } finally {
      setLoading(false);
    }
  }

  function handleEditClick() {
    setIsEditMode(true);
  }

  function handleCancelEdit() {
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
    } else {
      setIsEditMode(false);
    }
  }

  async function handleSave(data: Partial<Subscription>) {
    if (!id) return;
    try {
      await subscriptionsService.update(
        id,
        data as Parameters<typeof subscriptionsService.update>[1],
      );
      await loadSubscription(id);
      setIsEditMode(false);
      setHasUnsavedChanges(false);
      setToast({ message: "Subscription updated successfully", type: "success" });
      onMutationSuccess?.();
    } catch (err) {
      setToast({
        message: `Failed to update subscription: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    }
  }

  async function handleCancelSubscription() {
    if (!subscription) return;
    try {
      await subscriptionsService.cancel(subscription.id);
      setShowCancelConfirm(false);
      if (id) {
        await loadSubscription(id);
      }
      setToast({ message: "Subscription cancelled successfully", type: "success" });
      onMutationSuccess?.();
    } catch (err) {
      let errorMessage = "Failed to cancel subscription. Please try again.";
      if (err instanceof Error) {
        const errorWithDetails = err as Error & {
          details?: Array<{ code: string; path: (string | number)[]; message: string }>;
        };
        if (errorWithDetails.details && errorWithDetails.details.length > 0) {
          errorMessage = errorWithDetails.details.map(detail => detail.message).join(", ");
        } else {
          errorMessage = err.message;
        }
      }
      setShowCancelConfirm(false);
      setToast({ message: errorMessage, type: "error" });
    }
  }

  async function handleReactivateSubscription() {
    if (!subscription) return;
    try {
      await subscriptionsService.update(subscription.id, { status: "complete" });
      setShowReactivateConfirm(false);
      if (id) {
        await loadSubscription(id);
      }
      setToast({ message: "Subscription reactivated successfully", type: "success" });
      onMutationSuccess?.();
    } catch (err) {
      let errorMessage = "Failed to reactivate subscription. Please try again.";
      if (err instanceof Error) {
        const errorWithDetails = err as Error & {
          details?: Array<{ code: string; path: (string | number)[]; message: string }>;
        };
        if (errorWithDetails.details && errorWithDetails.details.length > 0) {
          errorMessage = errorWithDetails.details.map(detail => detail.message).join(", ");
        } else {
          errorMessage = err.message;
        }
      }
      setShowReactivateConfirm(false);
      setToast({ message: errorMessage, type: "error" });
    }
  }

  async function handleOnboardingComplete(data: OnboardingData) {
    if (!subscription?.id) return;
    setOnboardingError(null);
    try {
      await subscriptionsService.update(subscription.id, { metadata: data.metadata });
      setToast({ message: "Subscription configuration saved successfully", type: "success" });
      onMutationSuccess?.();
      if (id) {
        await loadSubscription(id);
      }
    } catch (err) {
      let errorMessage = "Failed to complete onboarding. Please try again.";
      if (err instanceof Error) {
        const errorWithDetails = err as Error & {
          details?: Array<{ code: string; path: (string | number)[]; message: string }>;
        };
        if (errorWithDetails.details && errorWithDetails.details.length > 0) {
          errorMessage = errorWithDetails.details.map(detail => detail.message).join("|");
        } else {
          errorMessage = err.message;
        }
      }
      setOnboardingError(errorMessage);
    }
  }

  /**
   * Enters edit mode for a single `metadataQuantities` section (e.g. `publication_edition_ids`,
   * `office_contact_pairs`) and seeds its picker state from that section's current value only —
   * sibling sections are never loaded into edit state, so a save can only ever patch this one key.
   */
  function handleEditMetadataSectionClick(key: string) {
    if (!subscription) return;
    const metadata = (subscription.metadata as Record<string, unknown> | undefined) || {};
    if (key === "office_contact_pairs") {
      setMetadataOfficeContactPairs((metadata.office_contact_pairs as OfficeContactPair[]) || []);
    } else {
      const value = metadata[key];
      setMetadataSelections({ [key]: Array.isArray(value) ? (value as (number | string)[]) : [] });
    }
    setMetadataSaveError(null);
    setEditingMetadataKey(key);
  }

  function handleCancelMetadataEdit() {
    setMetadataSaveError(null);
    setEditingMetadataKey(null);
  }

  async function handleSaveMetadata() {
    if (!subscription?.id || !editingMetadataKey) return;
    setIsSavingMetadata(true);
    setMetadataSaveError(null);
    try {
      const metadata: Record<string, unknown> =
        editingMetadataKey === "office_contact_pairs"
          ? { office_contact_pairs: metadataOfficeContactPairs }
          : { [editingMetadataKey]: metadataSelections[editingMetadataKey] || [] };
      await subscriptionsService.update(subscription.id, { metadata });
      setToast({ message: "Subscription metadata updated successfully", type: "success" });
      onMutationSuccess?.();
      setEditingMetadataKey(null);
      if (id) {
        await loadSubscription(id);
      }
    } catch (err) {
      let errorMessage = "Failed to update metadata. Please try again.";
      if (err instanceof Error) {
        const errorWithDetails = err as Error & {
          details?: Array<{ code: string; path: (string | number)[]; message: string }>;
        };
        if (errorWithDetails.details && errorWithDetails.details.length > 0) {
          errorMessage = errorWithDetails.details.map(detail => detail.message).join(", ");
        } else {
          errorMessage = err.message;
        }
      }
      setMetadataSaveError(errorMessage);
    } finally {
      setIsSavingMetadata(false);
    }
  }

  async function handleRemoveEdition() {
    if (!subscription || editionToRemove === null) {
      throw new Error("Publication edition is no longer available to remove.");
    }
    const current = (subscription.metadata as Record<string, unknown> | null)
      ?.publication_edition_ids;
    if (!Array.isArray(current)) {
      throw new Error("Publication editions have changed. Refresh the subscription and try again.");
    }

    const remaining = current.filter(value => String(value) !== String(editionToRemove));
    await subscriptionsService.update(subscription.id, {
      metadata: { publication_edition_ids: remaining },
    });
    setEditionToRemove(null);
    setMetadataSaveError(null);
    setToast({ message: "Publication edition removed", type: "success" });
    onMutationSuccess?.();
    if (id) await loadSubscription(id);
  }

  if (loading) {
    return <LoadingState message="Loading subscription..." />;
  }

  if (error || !subscription) {
    const status = error && (error as Error & { status?: number }).status;
    if (status === 401 || status === 403) {
      return <UnauthorizedView />;
    }
    return (
      <div className={styles.wrapper}>
        <EmptyState
          variant="error"
          title="Failed to load subscription"
          message={error?.message || "Subscription not found"}
          action={{
            label: "Back to Subscriptions",
            onClick: () => navigate("/fulfilment/subscriptions"),
          }}
        />
      </div>
    );
  }

  const daysRemaining = subscription.endDate ? calculateDaysRemaining(subscription.endDate) : null;
  const needsOnboarding = requiresOnboarding(subscription);

  const availableTabs: Array<{
    id: "overview" | "features" | "metadata" | "onboarding";
    label: string;
  }> = [
    { id: "overview", label: "Overview" },
    { id: "features", label: "Features" },
    { id: "metadata", label: "Metadata" },
    ...(needsOnboarding ? [{ id: "onboarding" as const, label: "Configuration" }] : []),
  ];

  if (isEditMode) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.editHeader}>
          <h1 className={styles.editTitle}>Edit Subscription</h1>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleCancelEdit}
            aria-label="Back to subscription detail"
          >
            <Icon icon="arrow-left" size="small" />
            <span>Back</span>
          </button>
        </div>
        <div className={styles.formContainer}>
          <SubscriptionForm
            subscription={subscription}
            onSave={handleSave}
            onCancel={handleCancelEdit}
            onDirtyChange={setHasUnsavedChanges}
            submitLabel="Save Changes"
          />
        </div>
        {showDiscardConfirm && (
          <UnsavedChangesModal
            onClose={() => setShowDiscardConfirm(false)}
            onConfirm={() => {
              setShowDiscardConfirm(false);
              setIsEditMode(false);
              setHasUnsavedChanges(false);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h1 className={styles.title}>
            <Link to={`/fulfilment/firms/${subscription.firmId}`}>
              {subscription.companyName
                ? `${subscription.companyName} (Firm ID: ${subscription.firmId})`
                : `Company ${subscription.firmId}`}
            </Link>
          </h1>
          <Tooltip
            title=""
            content={STATUS_DESCRIPTIONS[subscription.status] ?? subscription.status}
            position="top"
          >
            <span className={styles.statusBadge} data-status={subscription.status}>
              {subscription.status}
            </span>
          </Tooltip>
        </div>
        <div className={styles.headerActions}>
          <PermissionGate permission="fulfilment.edit_subscription">
            <Button
              variant="contained"
              color="error"
              onClick={() => setShowCancelConfirm(true)}
              disabled={subscription.status === "cancelled"}
            >
              <Icon icon="exclamation-circle" size="small" />
              Cancel Subscription
            </Button>
          </PermissionGate>
          {subscription.status === "cancelled" && (
            <PermissionGate permission="fulfilment.edit_subscription">
              {daysRemaining !== null && daysRemaining <= 0 ? (
                <Tooltip
                  title=""
                  content="Cannot reactivate an expired subscription. Please create a new one."
                  position="bottom"
                >
                  <Button variant="contained" color="primary" disabled>
                    <Icon icon="check" size="small" />
                    Reactivate Subscription
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setShowReactivateConfirm(true)}
                >
                  <Icon icon="check" size="small" />
                  Reactivate Subscription
                </Button>
              )}
            </PermissionGate>
          )}
          <PermissionGate permission="fulfilment.edit_subscription">
            <Button onClick={handleEditClick}>Edit Subscription</Button>
          </PermissionGate>
        </div>
      </div>

      {supplementaryErrors.length > 0 && (
        <div className={styles.errorBanner}>
          {supplementaryErrors.map(errMsg => (
            <p key={errMsg}>{errMsg}</p>
          ))}
        </div>
      )}

      <div className={styles.tabs}>
        {availableTabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={styles.tab}
            data-active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.content}>
        {activeTab === "overview" && (
          <>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Subscription Information</h2>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Company Name</span>
                  <span className={styles.infoValue}>
                    {subscription.companyName || <span className={styles.unmapped}>Unmapped</span>}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Company ID</span>
                  <span className={styles.infoValue}>{subscription.firmId}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Company Slug</span>
                  <span className={styles.infoValue}>
                    {subscription.companySlug || <span className={styles.nullValue}>Not set</span>}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>CRM Account ID</span>
                  <span className={styles.infoValue}>
                    {subscription.companyCrmEntityNumber || (
                      <span className={styles.nullValue}>Not set</span>
                    )}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Status</span>
                  <span className={styles.infoValue}>
                    <Tooltip
                      title=""
                      content={STATUS_DESCRIPTIONS[subscription.status] ?? subscription.status}
                      position="top"
                    >
                      <span>
                        {renderFieldValue(subscription as unknown as Subscription, "status")}
                      </span>
                    </Tooltip>
                    {daysRemaining !== null &&
                      daysRemaining > 0 &&
                      subscription.status !== "cancelled" && (
                        <span className={styles.daysRemaining}>
                          {" "}
                          ({daysRemaining} days remaining)
                        </span>
                      )}
                    {daysRemaining !== null &&
                      daysRemaining < 0 &&
                      subscription.status !== "cancelled" && (
                        <span className={styles.daysExpired}>
                          {" "}
                          (Expired {Math.abs(daysRemaining)} days ago)
                        </span>
                      )}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Package</span>
                  <span className={styles.infoValue}>
                    {subscription.packageId ? (
                      <Link
                        to={`/fulfilment/packages?search=${encodeURIComponent(subscription.packageId)}`}
                      >
                        {subscription.package?.productCode || "N/A"} (v
                        {subscription.package?.version || "1"})
                      </Link>
                    ) : (
                      <span className={styles.nullValue}>Not set</span>
                    )}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Start Date</span>
                  <span className={styles.infoValue}>
                    {renderFieldValue(subscription as unknown as Subscription, "startDate")}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>End Date</span>
                  <span className={styles.infoValue}>
                    {renderFieldValue(subscription as unknown as Subscription, "endDate")}
                  </span>
                </div>
              </div>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Placement</h2>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Site</span>
                  <span className={styles.infoValue}>
                    {subscription.site ? (
                      renderFieldValue(subscription as unknown as Subscription, "site")
                    ) : (
                      <span className={styles.nullValue}>Not set</span>
                    )}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Language</span>
                  <span className={styles.infoValue}>
                    {subscription.language ? (
                      renderFieldValue(subscription as unknown as Subscription, "language")
                    ) : (
                      <span className={styles.nullValue}>Not set</span>
                    )}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Country</span>
                  <span className={styles.infoValue}>
                    {subscription.siteStructureId ? (
                      <>
                        {placementRegionName || "Unknown Region"}
                        <span className={styles.idBadge}>ID: {subscription.siteStructureId}</span>
                      </>
                    ) : (
                      <span className={styles.nullValue}>Not set</span>
                    )}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Billing Region</span>
                  <span className={styles.infoValue}>
                    {subscription.billingTaxonomyTermId ? (
                      <>
                        {billingRegionName || "Unknown Region"}
                        <span className={styles.idBadge}>
                          ID: {subscription.billingTaxonomyTermId}
                        </span>
                      </>
                    ) : (
                      <span className={styles.nullValue}>Not set</span>
                    )}
                  </span>
                </div>
              </div>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Technical Details</h2>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Subscription ID</span>
                  <span className={styles.infoValue}>
                    <code className={styles.codeValue}>{subscription.id}</code>
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Package ID</span>
                  <span className={styles.infoValue}>
                    <code className={styles.codeValue}>{subscription.packageId}</code>
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Correlation ID</span>
                  <span className={styles.infoValue}>
                    {subscription.correlationId ? (
                      <code className={styles.codeValue}>{subscription.correlationId}</code>
                    ) : (
                      <span className={styles.nullValue}>None</span>
                    )}
                  </span>
                </div>
                {webhookLog && (
                  <>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>CRM Webhook Log</span>
                      <span className={styles.infoValue}>
                        <Link to={`/fulfilment/crm-webhook-logs/${webhookLog.id}`}>
                          View original CRM request
                        </Link>
                      </span>
                    </div>
                    {(webhookLog.rawPayload as Record<string, unknown>)?.metadata && (
                      <>
                        {(
                          (webhookLog.rawPayload as Record<string, unknown>).metadata as Record<
                            string,
                            unknown
                          >
                        )?.orderid && (
                          <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>CRM Order</span>
                            <span className={styles.infoValue}>
                              {String(
                                (
                                  (webhookLog.rawPayload as Record<string, unknown>)
                                    .metadata as Record<string, unknown>
                                ).orderid,
                              )}
                            </span>
                          </div>
                        )}
                        {(
                          (webhookLog.rawPayload as Record<string, unknown>).metadata as Record<
                            string,
                            unknown
                          >
                        )?.owner && (
                          <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>CRM Owner</span>
                            <span className={styles.infoValue}>
                              {String(
                                (
                                  (webhookLog.rawPayload as Record<string, unknown>)
                                    .metadata as Record<string, unknown>
                                ).owner,
                              )}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Created At</span>
                  <span className={styles.infoValue}>
                    {renderFieldValue(subscription as unknown as Subscription, "createdAt")}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Updated At</span>
                  <span className={styles.infoValue}>
                    {renderFieldValue(subscription as unknown as Subscription, "updatedAt")}
                  </span>
                </div>
              </div>
            </section>
          </>
        )}

        {activeTab === "features" && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Features</h2>
            {"features" in subscription.package &&
            subscription.package.features &&
            subscription.package.features.length > 0 ? (
              <div className={styles.featuresContent}>
                <p className={styles.featuresHeader}>
                  This subscription includes <strong>{subscription.package.features.length}</strong>{" "}
                  feature
                  {subscription.package.features.length !== 1 ? "s" : ""}:
                </p>
                <ul className={styles.featuresList}>
                  {subscription.package.features.map((feature: Feature) => (
                    <li key={feature.code} className={styles.featureItem}>
                      <Link
                        to={`/fulfilment/features?search=${encodeURIComponent(feature.code)}`}
                        className={styles.featureLink}
                      >
                        <div className={styles.featureDetails}>
                          <span className={styles.featureName}>{feature.name || feature.code}</span>
                          <code className={styles.featureCode}>{feature.code}</code>
                        </div>
                        <span className={styles.featureLinkIcon}>→</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className={styles.noData}>No features information available</p>
            )}
          </section>
        )}

        {activeTab === "metadata" && (
          <section className={styles.section}>
            <div className={styles.metadataHeader}>
              <h2 className={styles.sectionTitle}>Metadata</h2>
            </div>

            {metadataSaveError && (
              <div className={styles.errorBanner}>
                <p>{metadataSaveError}</p>
              </div>
            )}

            {(() => {
              const metadataQuantities = subscription.package?.metadataQuantities || {};
              const metadata = (subscription.metadata as Record<string, unknown> | undefined) || {};
              const configuredSectionKeys = Object.keys(metadataQuantities);
              const canEditMetadata =
                subscription.status !== "cancelled" && editingMetadataKey === null;
              const subscriptionFirmId = subscription.firmId;

              const features =
                "features" in subscription.package ? subscription.package.features : [];
              const featureEntityIdLabels = new Map<string, string>();
              for (const feature of features) {
                if (feature.entity_id_field && feature.entity_id_label) {
                  featureEntityIdLabels.set(feature.entity_id_field, feature.entity_id_label);
                }
              }
              /**
               * Legacy read-only label for a metadata key the current package no longer (or
               * never did) declare via `metadataQuantities` — preserves the plain "strip _ids,
               * replace underscores" formatting the Metadata tab has always used for these,
               * distinct from `metadataFieldLabel`'s richer (CUSTOM_METADATA_FIELD_LABELS-aware)
               * formatting used for the package's actual configurable sections.
               */
              function resolveLegacyFieldLabel(fieldName: string): string {
                return (
                  featureEntityIdLabels.get(fieldName) ||
                  fieldName.replace(/_ids$/, "").replace(/_/g, " ")
                );
              }

              function renderEntityIdList(fieldName: string, ids: (number | string)[]) {
                return (
                  <ul className={styles.featuresList}>
                    {ids.map(entityId => {
                      const matchedEntity = metadataEntityOptions[fieldName]?.find(
                        option => String(option.value) === String(entityId),
                      );
                      const resolvedName = metadataLabelForField(fieldName, entityId);
                      const displayName = resolvedName
                        ? `${resolvedName} · #${entityId}`
                        : `#${entityId}`;

                      if (fieldName === "contact_ids") {
                        const profileUrl = `https://www.legal500.com/lawyers/${entityId}/`;
                        return (
                          <li key={entityId} className={styles.featureItem}>
                            <a
                              href={profileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.featureLink}
                            >
                              <div className={styles.featureDetails}>
                                <span className={styles.featureName}>{displayName}</span>
                                {matchedEntity?.description && (
                                  <span className={styles.featureDescription}>
                                    {matchedEntity.description}
                                  </span>
                                )}
                              </div>
                              <span className={styles.featureLinkIcon}>→</span>
                            </a>
                          </li>
                        );
                      }

                      return (
                        <li key={entityId} className={styles.featureItem}>
                          {fieldName === "publication_edition_ids" ? (
                            <div className={styles.metadataEntityRow}>
                              <div className={styles.featureDetails}>
                                <span className={styles.featureName}>{displayName}</span>
                              </div>
                              {canEditMetadata && (
                                <PermissionGate permission="fulfilment.edit_subscription">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    color="error"
                                    onClick={() => setEditionToRemove(entityId)}
                                    aria-label={`Remove publication edition ${displayName}`}
                                  >
                                    Remove
                                  </Button>
                                </PermissionGate>
                              )}
                            </div>
                          ) : (
                            <div className={styles.featureDetails}>
                              <span className={styles.featureName}>{displayName}</span>
                              {fieldName === "editorial_category_ids" && matchedEntity?.group && (
                                <span className={styles.featureDescription}>
                                  Type: {matchedEntity.group}
                                </span>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                );
              }

              function renderOfficeContactPairsList(
                pairs: Array<{ office_id: number; contact_ids: number[] }>,
              ) {
                return (
                  <ul className={styles.featuresList}>
                    {pairs.map(pair => {
                      const office = officesMap.get(pair.office_id);
                      const officeLabel = office
                        ? `${office.city ?? office.name} · #${pair.office_id}`
                        : `#${pair.office_id}`;
                      return (
                        <li key={pair.office_id} className={styles.featureItem}>
                          <div className={styles.featureDetails}>
                            <span className={styles.featureName}>{officeLabel}</span>
                            {pair.contact_ids.length > 0 && (
                              <span className={styles.featureDescription}>
                                {pair.contact_ids
                                  .map(contactId => {
                                    const contact = contactsMap.get(contactId);
                                    return contact
                                      ? `${contact.name} · #${contactId}`
                                      : `#${contactId}`;
                                  })
                                  .join(", ")}
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                );
              }

              function renderMetadataSectionValue(key: string): React.ReactNode {
                if (key === "office_contact_pairs") {
                  const pairs =
                    (metadata.office_contact_pairs as
                      | Array<{ office_id: number; contact_ids: number[] }>
                      | undefined) ?? [];
                  return pairs.length > 0 ? (
                    renderOfficeContactPairsList(pairs)
                  ) : (
                    <p className={styles.noData}>Not configured</p>
                  );
                }

                const ids = metadata[key];
                return Array.isArray(ids) && ids.length > 0 ? (
                  renderEntityIdList(key, ids as (number | string)[])
                ) : (
                  <p className={styles.noData}>Not configured</p>
                );
              }

              function configuredSectionCount(key: string): number {
                const value = metadata[key];
                return Array.isArray(value) ? value.length : 0;
              }

              function renderConfiguredSection(key: string, requiredCount: number) {
                const count = configuredSectionCount(key);
                const label =
                  count > 0 ? `${metadataFieldLabel(key)} (${count})` : metadataFieldLabel(key);

                if (editingMetadataKey === key) {
                  return (
                    <div key={key}>
                      <h4 className={styles.metadataSubheading}>{label}</h4>
                      <MetadataFieldsEditor
                        firmId={subscriptionFirmId}
                        metadataQuantities={{ [key]: requiredCount }}
                        entityOptions={metadataEntityOptions}
                        entityErrors={metadataEntityErrors}
                        isLoadingEntities={isLoadingMetadataEntities}
                        fieldLabel={metadataFieldLabel}
                        selections={metadataSelections}
                        onSelectionsChange={(changedKey, values) =>
                          setMetadataSelections(prev => ({ ...prev, [changedKey]: values }))
                        }
                        officeContactPairs={metadataOfficeContactPairs}
                        onOfficeContactPairsChange={setMetadataOfficeContactPairs}
                        missingContactOffices={
                          (metadata.missing_contact_offices as Array<{
                            office_id: number;
                            city?: string | null;
                          }>) || []
                        }
                      />
                      <div className={styles.metadataActions}>
                        <Button
                          variant="outlined"
                          onClick={handleCancelMetadataEdit}
                          disabled={isSavingMetadata}
                        >
                          Cancel
                        </Button>
                        <Button onClick={handleSaveMetadata} disabled={isSavingMetadata}>
                          {isSavingMetadata ? "Saving..." : "Save"}
                        </Button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={key}>
                    <div className={styles.metadataSectionHeader}>
                      <h4 className={styles.metadataSubheadingInline}>{label}</h4>
                      {canEditMetadata && (
                        <PermissionGate permission="fulfilment.edit_subscription">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleEditMetadataSectionClick(key)}
                            aria-label={`Edit ${label}`}
                          >
                            <Icon icon="edit" size="small" />
                            Edit
                          </Button>
                        </PermissionGate>
                      )}
                    </div>
                    {renderMetadataSectionValue(key)}
                  </div>
                );
              }

              const legacyEntityIdFields = Object.keys(metadata).filter(
                key =>
                  key.endsWith("_ids") &&
                  Array.isArray(metadata[key]) &&
                  !configuredSectionKeys.includes(key),
              );
              const legacyOfficeContactPairs =
                !configuredSectionKeys.includes("office_contact_pairs") &&
                Array.isArray(metadata.office_contact_pairs) &&
                (metadata.office_contact_pairs as unknown[]).length > 0
                  ? (metadata.office_contact_pairs as Array<{
                      office_id: number;
                      contact_ids: number[];
                    }>)
                  : null;

              const internalMetadataKeys = new Set([
                "missing_contact_offices",
                "office_contact_pairs",
                "actual_features",
                "onboarding_complete",
              ]);
              const otherMetadata = Object.entries(metadata).filter(
                ([key]) =>
                  !key.endsWith("_ids") &&
                  !internalMetadataKeys.has(key) &&
                  !configuredSectionKeys.includes(key),
              );
              const missingOffices = metadata.missing_contact_offices as
                | Array<{ office_id: number; city?: string | null }>
                | undefined;

              const hasAnyDisplayableMetadata =
                configuredSectionKeys.length > 0 ||
                legacyEntityIdFields.length > 0 ||
                legacyOfficeContactPairs !== null ||
                otherMetadata.length > 0;

              if (!hasAnyDisplayableMetadata) {
                return <p className={styles.noData}>No metadata available</p>;
              }

              const entityIdFieldsWithData = Object.keys(metadata).filter(
                key =>
                  key.endsWith("_ids") &&
                  Array.isArray(metadata[key]) &&
                  (metadata[key] as unknown[]).length > 0,
              );
              const summaryParts = entityIdFieldsWithData.map(fieldName => {
                const ids = metadata[fieldName] as unknown[];
                const label = resolveLegacyFieldLabel(fieldName);
                return `${ids.length} ${label.toLowerCase()}${ids.length !== 1 ? "s" : ""}`;
              });

              return (
                <div className={styles.featuresContent}>
                  <p className={styles.featuresHeader}>
                    This subscription includes placement metadata
                    {summaryParts.length > 0 ? ` for ${summaryParts.join(" and ")}` : ""}:
                  </p>

                  {configuredSectionKeys.map(key =>
                    renderConfiguredSection(key, metadataQuantities[key]),
                  )}

                  {legacyOfficeContactPairs !== null && (
                    <div>
                      <h4 className={styles.metadataSubheading}>
                        Office/Contact Pairs ({legacyOfficeContactPairs.length})
                      </h4>
                      {renderOfficeContactPairsList(legacyOfficeContactPairs)}
                    </div>
                  )}

                  {legacyEntityIdFields.map(fieldName => {
                    const ids = metadata[fieldName] as (number | string)[];
                    return (
                      <div key={fieldName}>
                        <h4 className={styles.metadataSubheading}>
                          {resolveLegacyFieldLabel(fieldName)} ({ids.length})
                        </h4>
                        {renderEntityIdList(fieldName, ids)}
                      </div>
                    );
                  })}

                  {Array.isArray(missingOffices) && missingOffices.length > 0 && (
                    <p className={styles.noData}>
                      Note: {missingOffices.length} office
                      {missingOffices.length !== 1 ? "s" : ""} excluded from sync (no contact data):{" "}
                      {missingOffices
                        .map(o => (o.city ? `${o.city} (${o.office_id})` : String(o.office_id)))
                        .join(", ")}
                    </p>
                  )}

                  {otherMetadata.length > 0 && (
                    <>
                      <h4 className={styles.metadataSubheading}>Additional Metadata</h4>
                      <div className={styles.jsonDisplay}>
                        <pre>{JSON.stringify(Object.fromEntries(otherMetadata), null, 2)}</pre>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </section>
        )}

        {activeTab === "onboarding" && needsOnboarding && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Configuration</h2>
            <OnboardingTab
              subscription={subscription}
              onComplete={handleOnboardingComplete}
              onCancel={() => setActiveTab("overview")}
              apiError={onboardingError}
            />
          </section>
        )}
      </div>

      {showCancelConfirm && (
        <ConfirmationModal
          title="Cancel Subscription"
          message="Are you sure you want to cancel this subscription?"
          warningMessage="This will set the subscription status to 'cancelled'. If needed, the subscription can be reactivated later."
          affectedItems={[
            {
              id: subscription.id,
              name: subscription.companyName
                ? `${subscription.companyName} (Firm ID: ${subscription.firmId})`
                : `Company ${subscription.firmId}`,
              code: subscription.package?.productCode,
            },
          ]}
          confirmButtonText="Cancel Subscription"
          confirmButtonColor="error"
          cancelButtonText="Keep Subscription"
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={handleCancelSubscription}
        />
      )}

      {editionToRemove !== null && (
        <ConfirmationModal
          title="Remove Publication Edition"
          message={`Remove publication edition #${editionToRemove} from this subscription?`}
          warningMessage="This removes the edition from subscription metadata. Removing the last edition leaves no publication editions on this subscription."
          confirmButtonText="Remove Edition"
          confirmButtonColor="error"
          cancelButtonText="Keep Edition"
          onClose={() => setEditionToRemove(null)}
          onConfirm={handleRemoveEdition}
        />
      )}

      {showReactivateConfirm && (
        <ConfirmationModal
          title="Reactivate Subscription"
          message="Are you sure you want to reactivate this subscription?"
          warningMessage="This will set the subscription status back to 'complete', making it active again."
          affectedItems={[
            {
              id: subscription.id,
              name: subscription.companyName
                ? `${subscription.companyName} (Firm ID: ${subscription.firmId})`
                : `Company ${subscription.firmId}`,
              code: subscription.package?.productCode,
            },
          ]}
          confirmButtonText="Reactivate Subscription"
          cancelButtonText="Keep Cancelled"
          onClose={() => setShowReactivateConfirm(false)}
          onConfirm={handleReactivateSubscription}
        />
      )}
    </div>
  );
}

export default SubscriptionDetail;
